import { CONSENT_TYPES, type ConsentState, type ConsentTypeCode } from '@/lib/api/consents/types';

export type ConsentPrompt =
  | { kind: 'none' }
  | {
      kind: 'prompt';
      title: string;
      description: string;
      missing: ConsentTypeCode[];
      showElectronicDelivery: boolean;
      showTextAlerts: boolean;
      showAutomatedCalling: boolean;
      showMobileNumber: boolean;
    };

const TITLE = {
  edAndComms: 'Electronic Delivery and Communication Preference',
  edOnly: 'Electronic Delivery Consent',
  commsOnly: 'Communication Preference',
} as const;

const DESCRIPTION = {
  edAndComms:
    "Review the information below to accept electronic delivery and choose how you'd like to receive policy and account updates.",
  edOnly: 'Review and accept electronic delivery to receive eligible documents electronically.',
  bothComms: "Choose how you'd like to receive policy and account updates.",
  smsOnly: 'Stay informed about your policy and account with text alerts.',
  aiOnly: 'Stay informed about your policy and account with automated calling alerts.',
} as const;

const isMissing = (state: ConsentState, consentType: ConsentTypeCode): boolean =>
  state[consentType]?.response === 'NO_RESPONSE';

export const deriveConsentPrompt = (state: ConsentState): ConsentPrompt => {
  const missing = CONSENT_TYPES.filter((consentType) => isMissing(state, consentType));

  if (missing.length === 0) return { kind: 'none' };

  const showElectronicDelivery = missing.includes('ELECTRONIC_DELIVERY');
  const showTextAlerts = missing.includes('TRANSACTIONAL_SMS');
  const showAutomatedCalling = missing.includes('AI_CALLING');
  const showMobileNumber = showTextAlerts || showAutomatedCalling;

  if (showElectronicDelivery && showMobileNumber) {
    return {
      kind: 'prompt',
      title: TITLE.edAndComms,
      description: DESCRIPTION.edAndComms,
      missing,
      showElectronicDelivery,
      showTextAlerts,
      showAutomatedCalling,
      showMobileNumber,
    };
  }

  if (showElectronicDelivery) {
    return {
      kind: 'prompt',
      title: TITLE.edOnly,
      description: DESCRIPTION.edOnly,
      missing,
      showElectronicDelivery,
      showTextAlerts,
      showAutomatedCalling,
      showMobileNumber,
    };
  }

  const description =
    showTextAlerts && showAutomatedCalling
      ? DESCRIPTION.bothComms
      : showTextAlerts
        ? DESCRIPTION.smsOnly
        : DESCRIPTION.aiOnly;

  return {
    kind: 'prompt',
    title: TITLE.commsOnly,
    description,
    missing,
    showElectronicDelivery,
    showTextAlerts,
    showAutomatedCalling,
    showMobileNumber,
  };
};
